import React from "react";

import AlertComponent from "@mui/material/Alert";

import { Alert } from "hooks/use-alert-bar.hook";

interface CustomAlertProps {
  alert: Alert;
  onClose: (id: number | string) => void;
}

/**
 * Centre the severity icon and the close button against the row.
 *
 * MUI's own defaults assume an alert's message is a line of text: `.MuiAlert-icon` carries
 * `padding: 7px 0` chosen to match a 14px line's `8px`, and `.MuiAlert-action` is
 * explicitly `align-items: flex-start` with a 4px top pad. The root has no `align-items`,
 * so it stretches.
 *
 * That holds until a message contains controls. The patient-arrival alert puts two 30px
 * buttons in its message, which makes the message block roughly twice the height of a text
 * line — and the icon and the close button both stay pinned near the top, visibly high against
 * a row whose text is centred. Chen raised it on 2026-09-17 looking at that banner.
 *
 * Only the icon and the action move. The message keeps MUI's vertical padding on purpose:
 * removing it would shorten every text-only alert in the app by 16px, and this is a shared
 * component with other callers (`patient-alerts/alert-dialog.component.tsx`), not just the
 * arrival banner.
 *
 * No `&&` needed here, unlike the buttons and chips: the app theme has no `MuiAlert`
 * override, so a plain `sx` outranks MUI's single-class defaults.
 */
export default function CustomAlert({ alert, onClose }: CustomAlertProps) {
  return (
    <AlertComponent
      variant="filled"
      severity={alert.severity}
      onClose={() => onClose(alert.id)}
      sx={{
        alignItems: "center",
        "& .MuiAlert-icon": { alignItems: "center", py: 0 },
        "& .MuiAlert-action": { alignItems: "center", pt: 0 }
      }}
    >
      {alert.message}
    </AlertComponent>
  );
}
